import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, DataTable, Dialog, Typography } from '@scope/scope-one-ui/core';
import { ColumnDefinition } from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import { COLORS } from '@scope/scope-one-ui/theme';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import { AddUserVariant, AddUserVariants, Rating, SubscriptionRatingType, User } from '../types';
import { useRSR } from '../../../../shared/hooks';
import { addUser, getRatings, getUsers, updateUserRatings } from '../services/rsrService';
import { validateEmail } from '../../../../shared/functions/validators/validateEmail';

export type AddUserRSRProps = {
  ratingType: SubscriptionRatingType;
  variant: AddUserVariant;
  user?: User;
  open: boolean;
  onClose: (added: boolean) => void;
};

export const AddUserRSR: FC<AddUserRSRProps> = ({
  ratingType,
  open,
  onClose,
  user: propUser,
  variant,
}) => {
  const { t } = useTranslation();
  const { error, success } = useToasts();
  const [user, setUser] = useState<User | undefined>(propUser);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const initialColumns: ColumnDefinition[] = [
    {
      name: 'entityName',
      label: t('ADMIN.RSR.RSR_NAME'),
      sortable: true,
      customBodyRender: (v) => <div>{v.entityName}</div>,
    },
    {
      name: 'ratingType',
      label: t('ADMIN.RSR.RATING_TYPE'),
      sortable: true,
    },
  ];

  const {
    setTotal,
    setRows,
    sort,
    pagination,
    rows,
    isSelectedAll,
    selectAllToggle,
    selectedIds,
    selectToggle,
    clearSelectedIds,
    setSelectedIds,
    tableActions,
  } = useRSR({ initialColumns, defaultSortField: 'entityName' });

  useEffect(() => {
    clear();
    setUser(propUser);
  }, [propUser]);

  useEffect(() => {
    if (!user?.approvedRatings) return;

    const approved = user.approvedRatings.map((rating) => rating.entityId);
    setSelectedIds(approved);
  }, [user]);

  const buildRows = (data: Rating[]) =>
    data.map((entity) => {
      const { entityId, entityName, entityType, ratingType: entityRatingType, ratingId } = entity;
      return {
        id: entityId,
        data: {
          entityName: { entityId, entityName },
          entityType,
          ratingType: entityRatingType,
          ratingId,
        },
      };
    });

  useEffect(() => {
    setLoading(true);
    getRatings(ratingType, pagination.page, pagination.limit, {
      sort: sort.field,
      dir: sort.order,
    })
      .then((resp) => {
        setRows(buildRows(resp.data));
        setTotal(resp.total);
      })
      .finally(() => setLoading(false));
  }, [ratingType, pagination, sort]);

  const submitHandler = async () => {
    const ratings = rows
      .filter((row) => selectedIds.includes(row.id))
      .map((row) => ({
        entityId: row.id,
        ratingId: row.data.ratingId,
        entityType: row.data.entityType,
      }));
    // INVITE
    if (variant === AddUserVariants.INVITE && ratings.length && (user || email)) {
      addUser(ratingType, user?.email ?? email, ratings)
        .then((response) => {
          success({ title: '', content: t('ADMIN.SNACKS.USER_ADDED') });
          clear();
          onClose(true);
        })
        .catch((e) => {
          if (e.responseCode === 409) {
            error({ title: '', content: e.message });
          } else {
            error({ title: '', content: t('ADMIN.SNACKS.USER_ADD_FAILED') });
          }
        });
    }
    // PERMISSIONS
    else if (user && user.userId) {
      updateUserRatings(ratingType, user?.userId, ratings)
        .then((response) => {
          success({ title: '', content: t('ADMIN.SNACKS.USER_UPDATED') });
          clear();
          onClose(true);
        })
        .catch((e) => {
          error({ title: '', content: t('ADMIN.SNACKS.USER_UPDATE_FAILED') });
        });
    }
  };

  const [users, setUsers] = useState<User[]>();

  const options = users?.map((u) => u.email);

  const onSearch = (searchedValue: string | number) => {
    setEmail(searchedValue.toString());
    getUsers(ratingType, 1, 10, undefined, searchedValue.toString()).then((resp) =>
      setUsers(resp.data),
    );
  };

  const onSelect = (mail?: string | number) => setUser(users?.find((u) => u.email === mail));

  const clear = () => {
    onSelect();
    clearSelectedIds();
    setUser(undefined);
  };

  const isValid = (user || validateEmail(email)) && selectedIds.length > 0;

  const renderAutocomplete = () => (
    <Autocomplete
      label="Email address"
      placeholder="Type email address"
      noOptionsText=""
      options={options}
      selectedValue={user?.email || ''}
      onSelect={onSelect}
      onSearch={onSearch}
    />
  );
  const renderUserBox = () => {
    const getUserName = () => {
      if (user && user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`;
      }
      return '-';
    };

    const data = [
      { label: 'Name', value: getUserName() },
      { label: 'Organization', value: user?.organisationName ?? '-' },
      { label: 'Email', value: user?.email ?? '-' },
    ];

    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pr={5}>
        {data.map((col) => (
          <Box gridGap={2}>
            <Typography variant="body1" color={COLORS.typography.main}>
              {col.label}
            </Typography>
            <Typography variant="h3">{col.value}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      size="medium"
      onConfirm={submitHandler}
      cancelLabel="Close"
      confirmLabel={variant === AddUserVariants.INVITE ? 'Invite' : 'Confirm'}
      disabled={!isValid}
      title={t(
        variant === AddUserVariants.INVITE ? 'ADMIN.USERS.ADD_USER' : 'ADMIN.USERS.EDIT_USER',
      )}
      open={open}
      onClose={() => onClose(false)}
    >
      <Box display="flex" gridGap={1} flexDirection="column">
        <Box py={2} px={3} backgroundColor={COLORS.background.control}>
          {variant === AddUserVariants.INVITE ? renderAutocomplete() : renderUserBox()}
        </Box>
        <Box px={3}>
          <DataTable
            maxHeight={60}
            stickyHeader
            selectable
            cols={initialColumns}
            loading={loading}
            selection={{
              isSelectedAll,
              selectAllToggle,
              selectedIds,
              selectToggle,
            }}
            tableActions={tableActions}
            rows={rows}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
